import type { AiChatMessage } from "@productlane/lib/src/ai"
import { Button, Dialog, useBreakpoints } from "@productlane/ui"

import { ReviewDiff } from "@/screens/app/helpCenter/review-diff"

type ContentRewrite = NonNullable<AiChatMessage["contentRewrite"]>

const TARGET_LABEL: Record<ContentRewrite["target"], string> = {
	composer: "reply",
	article: "article",
	changelog: "changelog entry",
}

export interface CopilotRewritePreview {
	rewrite: ContentRewrite
	baseHtml: string
	title: string
	icon: string
}

// The same split view a docs suggestion is reviewed in, pointed at a copilot
// rewrite that has no proposal row behind it yet.
export const CopilotRewritePreviewDialog = ({
	preview,
	onOpenChange,
	onApply,
}: {
	preview: CopilotRewritePreview | null
	onOpenChange: (open: boolean) => void
	onApply: () => void
}) => {
	const { isMobile } = useBreakpoints()

	return (
		<Dialog
			isOpen={preview !== null}
			onOpenChange={onOpenChange}
			size="xl"
			title={preview?.rewrite.title ?? "Proposed changes"}
			description={
				preview
					? `Current ${TARGET_LABEL[preview.rewrite.target]} on the left, the rewrite on the right.`
					: undefined
			}
			scrollContainerClasses="max-h-[70dvh]"
			footer={
				<div className="flex items-center justify-end gap-2">
					<Button
						variant="outline"
						onClick={() => onOpenChange(false)}
						className="rounded-lg"
					>
						Close
					</Button>
					<Button onClick={onApply} className="rounded-lg">
						Apply
					</Button>
				</div>
			}
		>
			{preview && (
				<ReviewDiff
					layout={isMobile ? "flat" : "split"}
					icon={preview.icon}
					articleTitle={preview.title}
					baseHtml={preview.baseHtml}
					mergedHtml={preview.rewrite.html}
					fallback={
						<p className="py-6 text-center text-sm text-secondary">
							This rewrite is too large to pair block by block. Apply it to see
							the change in the editor.
						</p>
					}
				/>
			)}
		</Dialog>
	)
}
